/**
 * The scroll between two levels (spec 05 §3.2) as a dev scene: level n above level n+1, the
 * camera moving down from one to the other at the C64's pace, the LUT built from both levels' colours.
 *
 *   ?scene=drop&level=1&p=0.4     frozen at 40 % of the way down
 *   ?scene=drop&level=1           animated
 *
 * `__look.stability(p0, p1)` renders both positions and returns the share of the overlapping
 * static pixels that differ: the shimmer measure (NIGHTDROP 05 §12 used the same test).
 */

import { Scene } from 'three';
import stageGrade from '../../content/grades/stage.json';
import { level } from '../levels';
import { gridFromRows } from '../levels/grid';
import { StageCamera } from '../render/camera/StageCamera';
import { levelLook } from '../render/levelLook';
import { createGradientMap } from '../render/materials/toon';
import { type Grade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { createStageLights, StageView } from '../render/stage/StageView';
import { chooseSize, createRenderer, exposeLook, num } from './devKit';

/** The C64's scroll: 32 frames at 50 Hz, a constant pace (spec 02 §9.1). */
const DROP_SECONDS = 32 / 50;

export function startDropdev(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const n = Math.max(1, Math.min(99, Math.round(num(params, 'level', 1))));
  const grade = stageGrade as Grade;
  const gradientMap = createGradientMap();
  const renderer = createRenderer(canvas);
  const scene = new Scene();

  const defs = [level(n), level(n + 1)];
  const palettes = defs.map((d) => new Palette(levelLook(d)));
  const [upper, lower] = palettes;
  if (!upper || !lower) throw new Error('no palettes');
  const height = defs[0]?.tiles.length ?? 25;
  defs.forEach((def, i) => {
    const palette = palettes[i] ?? upper;
    const stage = new StageView({
      grid: gridFromRows(def.tiles), style: levelLook(def).style, palette, gradientMap, seed: def.n,
    });
    stage.group.position.y = -height * i;
    scene.add(stage.group);
  });
  const lights = createStageLights(grade, 32, height * 2);
  lights.position.y = -height / 2;
  scene.add(lights);

  const size = chooseSize(params);
  const camera = new StageCamera(size, num(params, 'ppt', 14) * size.scale, num(params, 'pitch', 18));
  const pipeline = new RenderPipeline(renderer, upper.buildLut(lower.all()), upper.color('ink.0'), size);
  const { targetW: TARGET_W, targetH: TARGET_H } = size;
  pipeline.setGrade(grade, upper.fog);

  const place = (p: number): void => {
    camera.focus.set(0, -p * height, 0);
    camera.update();
  };
  const draw = (p: number): void => {
    place(p);
    pipeline.render(scene, camera);
  };

  const frozen = params.has('p');
  const start = performance.now();
  const frame = (): void => {
    const t = (performance.now() - start) / 1000;
    // Hold each level for 1.5 s either side of the scroll, so the loop is watchable.
    const p = frozen ? num(params, 'p', 0) : Math.min(1, Math.max(0, ((t % (DROP_SECONDS + 3)) - 1.5) / DROP_SECONDS));
    draw(p);
    if (!frozen) requestAnimationFrame(frame);
  };
  frame();

  exposeLook(pipeline, frame, {
    camera,
    /** Share of overlapping pixels that differ between the frames at p0 and p1, once aligned. */
    stability(p0: number, p1: number): number {
      draw(p0);
      const a = pipeline.readPixels();
      const ay = camera.camTexel.y;
      draw(p1);
      const b = pipeline.readPixels();
      const shift = camera.camTexel.y - ay;
      let differ = 0;
      let total = 0;
      for (let y = 2; y < TARGET_H - 2; y++) {
        const yb = y - shift;
        if (yb < 2 || yb >= TARGET_H - 2) continue;
        for (let x = 2; x < TARGET_W - 2; x++) {
          const ia = (y * TARGET_W + x) * 4;
          const ib = (yb * TARGET_W + x) * 4;
          total++;
          if (a[ia] !== b[ib] || a[ia + 1] !== b[ib + 1] || a[ia + 2] !== b[ib + 2]) differ++;
        }
      }
      return total === 0 ? 0 : differ / total;
    },
  });
}
